import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ContainerProps } from './model';

/**
 * Common properties used in both the input to the `ContainerContextProvider`
 * and the `ContainerContextValue`. The `ContainerContextProvider` takes in
 * values that it then transforms into the `ContainerContextValue` for the
 * context.
 */
interface ContainerContextCommon
  extends Pick<
    ContainerProps,
    | 'actions'
    | 'actionsSize'
    | 'collapsed'
    | 'collapseButtonAriaLabel'
    | 'defaultCollapsed'
    | 'menuItems'
    | 'onCollapseButtonClick'
    | 'subtleActionsVisibility'
    | 'defaultModalOpen'
    | 'modalOpen'
    | 'onOpenModalButtonClick'
    | 'onCloseModal'
    | 'isLoading'
    | 'isFetching'
    | 'draggable'
  > {
  /**
   * When true, the `Container` is nested within another `Container`.
   */
  isNestedContainer: boolean;
}

/**
 * value for container context.
 */
export interface ContainerContextValue extends ContainerContextCommon {
  isCollapsible: boolean;
  isHovered: boolean;
  isMenuOpen: boolean;
  isModalEnabled: boolean;
  isShowingFloatingActions: boolean;
  setIsHovered: (isHovered: boolean) => void;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setIsShowingFloatingActions: (isShowingFloatingActions: boolean) => void;
}
const ContainerContext = createContext<null | ContainerContextValue>(null);

/**
 * `value` prop passed into the `ContainerContextProvider`, which then transforms
 * it into `ContainerContextValue` for the context.
 */
export interface ContainerContextProviderValue extends ContainerContextCommon {}

export interface ContainerContextProviderProps {
  children: React.ReactNode;
  value: ContainerContextProviderValue;
}

/**
 * Context provide that helps manage container state handling between the parent `Container` and
 * its children (`Container.Header`, `Container.Body`).
 */
export function ContainerContextProvider({ children, value: initialValue }: ContainerContextProviderProps) {
  const {
    collapsed: _collapsed,
    collapseButtonAriaLabel,
    defaultCollapsed,
    onCollapseButtonClick: onControlledCollapseButtonClick,
    actions,
    subtleActionsVisibility,
    actionsSize,
    menuItems,
    modalOpen,
    defaultModalOpen,
    onOpenModalButtonClick: onControlledOpenModalButtonClick,
    onCloseModal: onControlledCloseModal,
    draggable,
    isLoading,
    isFetching,
    isNestedContainer,
  } = initialValue;

  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShowingFloatingActions, setIsShowingFloatingActions] = useState(false);

  const hasControlledCollapsed = typeof _collapsed !== 'undefined';
  const hasUncontrolledCollapsed = typeof defaultCollapsed !== 'undefined';
  const [internalCollapsed, setInternalCollapsed] = useState<undefined | boolean>(
    hasControlledCollapsed ? undefined : defaultCollapsed
  );

  const toggleInternalCollapsed = useCallback(() => {
    setInternalCollapsed((prevCollapsed) => {
      return !prevCollapsed;
    });
  }, []);

  const hasControlledOpenModal = typeof modalOpen !== 'undefined';
  const hasUncontrolledOpenModal = typeof defaultModalOpen !== 'undefined';
  const [internalOpenModal, setInternalOpenModal] = useState<undefined | boolean>(
    hasControlledOpenModal ? undefined : defaultModalOpen
  );
  const internalOnOpenModal = useCallback(() => {
    setInternalOpenModal(true);
  }, []);
  const internalOnCloseModal = useCallback(() => {
    setInternalOpenModal(false);
  }, []);

  const value: ContainerContextValue = useMemo(() => {
    const isModalOpenValue = hasControlledOpenModal ? modalOpen : internalOpenModal;
    const collapsed = hasControlledCollapsed ? _collapsed : internalCollapsed;
    const onCollapseButtonClick =
      hasUncontrolledCollapsed && !hasControlledCollapsed ? toggleInternalCollapsed : onControlledCollapseButtonClick;

    return {
      isLoading,
      isFetching,
      collapsed,
      collapseButtonAriaLabel,
      onCollapseButtonClick,
      actions,
      draggable,
      subtleActionsVisibility,
      isCollapsible: !!(collapsed !== undefined && onCollapseButtonClick && !modalOpen),
      isHovered,
      setIsHovered,
      isMenuOpen,
      setIsMenuOpen,
      actionsSize,
      isShowingFloatingActions,
      setIsShowingFloatingActions,
      menuItems,
      modalOpen: isModalOpenValue,
      isModalEnabled: typeof isModalOpenValue !== 'undefined',
      onOpenModalButtonClick:
        hasUncontrolledOpenModal && !hasControlledOpenModal ? internalOnOpenModal : onControlledOpenModalButtonClick,
      onCloseModal: hasUncontrolledOpenModal && !hasControlledOpenModal ? internalOnCloseModal : onControlledCloseModal,
      isNestedContainer,
    };
  }, [
    hasControlledCollapsed,
    _collapsed,
    collapseButtonAriaLabel,
    internalCollapsed,
    hasUncontrolledCollapsed,
    toggleInternalCollapsed,
    onControlledCollapseButtonClick,
    actions,
    draggable,
    subtleActionsVisibility,
    isHovered,
    isMenuOpen,
    actionsSize,
    isShowingFloatingActions,
    menuItems,
    hasControlledOpenModal,
    modalOpen,
    internalOpenModal,
    hasUncontrolledOpenModal,
    internalOnOpenModal,
    onControlledOpenModalButtonClick,
    internalOnCloseModal,
    onControlledCloseModal,
    isLoading,
    isFetching,
    isNestedContainer,
  ]);

  return <ContainerContext.Provider value={value}>{children}</ContainerContext.Provider>;
}

// Bare minimum to avoid breaking things if for some reason context is missing
// (e.g. in the Header storybook).
const EMPTY_CONTAINER_CONTEXT: ContainerContextValue = {
  isCollapsible: false,
  isHovered: false,
  isMenuOpen: false,
  isModalEnabled: false,
  setIsHovered: () => {},
  setIsMenuOpen: () => {},
  actionsSize: 'medium',
  isShowingFloatingActions: false,
  setIsShowingFloatingActions: () => {},
  isNestedContainer: false,
};

export function useHasContainerContext() {
  const context = useContext(ContainerContext);
  return !!context;
}

export function useContainerContext() {
  const context = useContext(ContainerContext);

  return context || EMPTY_CONTAINER_CONTEXT;
}

interface ContainerModalContextValue {
  /**
   * Is the container opened in a modal directly (i.e. it is the top-level container in the modal).
   */
  isInModal: boolean;
  /**
   * Is the container in a modal as a child (i.e. because its parent was opened in a modal). Useful in
   * cases where a nested container may want to behave differently in a modal.
   */
  isChildContainerInModal?: boolean;
}

export interface ContainerModalContextProviderProps {
  children: React.ReactNode;
  value: ContainerModalContextValue;
}

// This extra context enables us to clarify whether a container is inside of a modal and its position
// in the tree of that modal. This is useful for several reasons:
// - We want to override CSS only on the parent container (i.e. the container that triggered the modal
//   opening) to ensure it fills the modal. If we apply these styles to child containers within that container,
//   we can accidentally break the CSS of nested containers within that container.
// - When using the "clone" strategy, we cannot rely on whether the container's `openInModal` is
//   set to `true` because we render the container twice: once in the modal and once inline. We want the version
//   that's rendered inline to continue behaving like it's not in a modal despite that state.
const ContainerModalContext = createContext<null | ContainerModalContextValue>(null);
export const ContainerModalContextProvider = function ContainerModalContextProvider({
  children,
  value,
}: ContainerModalContextProviderProps) {
  // If the container is in a modal before we create this new context, we can infer this is a
  // child of a container that was opened in a modal. We want to make a distinction between the
  // container that triggered the modal opening (i.e. the top-level container in the modal) and any
  // child containers it may have because we need to apply different styles to them and consumers
  // may want to apply different behaviors.
  const { isInModal } = useIsContainerInModal();

  const contextValue = useMemo(() => {
    return {
      ...value,
      isChildContainerInModal: isInModal,
    };
  }, [value, isInModal]);

  return <ContainerModalContext.Provider value={contextValue}>{children}</ContainerModalContext.Provider>;
};

type UseIsContainerInModalResponse = {
  /**
   * Returns true if this container is in a modal as a child nested below the top-level container that triggered the
   * opening of the modal. Useful in cases where you only want to modify the nested children but not the parent
   * container.
   */
  isChildContainerInModal: boolean;

  /**
   * Returns true if the container is in a modal, regardless of its location in the tree. Useful in most cases where
   * you want a container to behave differently depending on if it is in a modal or not.
   */
  isInModal: boolean;

  /**
   * Returns true of this container is in a modal as the parent container. In other words, this is the container
   * that triggered the opening of the modal. Useful in cases where you only want to modify the parent container, but
   * not any of its nested children.
   */
  isParentContainerInModal: boolean;
};

/**
 * Returns information about if the container is open in a modal.
 *
 * This context should primarily be used for implementation of other design
 * system features that are closely dependent on `Container` (e.g. `VizPanel`).
 */
export const useIsContainerInModal = (): UseIsContainerInModalResponse => {
  const context = useContext(ContainerModalContext);
  const isParentContainerInModal = context?.isInModal ?? false;
  const isChildContainerInModal = context?.isChildContainerInModal ?? false;

  return {
    isParentContainerInModal,
    isChildContainerInModal,
    isInModal: isParentContainerInModal || isChildContainerInModal,
  };
};

/**
 * Returns the collapsed state of the container. When `true`, the container is collapsed.
 */
export const useIsContainerCollapsed = () => {
  const context = useContainerContext();
  return !!context.collapsed;
};
